import type { Ref, WritableComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { AppTextareaEvent } from '@shared/ui/fields'
import type { DisciplineActivationData, DisciplineActivationForm } from './interfaces'

import { useListsStore } from '@shared/model'
import { useToast } from '@shared/ui/toast'

import { DisciplineKanbanTab } from './enums'
import { useDisciplineStore } from './store'
import { useDisciplineActivationUpdateApi } from '../api/activation-update.hook'
import { useDisciplineKanbanUrl } from './discipline-kanban-url.hook'
import { useDisciplineRefresh } from './discipline-refresh.hook'
import {
  buildActivationPayload,
  createActivationForm,
  getActivationExpectationLabels,
  getActivationScheduleLines,
  getActivationStartDate,
  isActivationAgentScheduleCleared,
} from './discipline-activation'

interface DisciplineActivationReturn {
  refForm: Ref<DisciplineActivationForm>
  refExpectations: WritableComputedRef<string[]>
  refKanbanUrl: ComputedRef<string | null>
  refIsSaving: Ref<boolean>
  pace: ComputedRef<number>
  scheduleLines: ComputedRef<string[]>
  startDate: ComputedRef<string>
  expectationOptions: ComputedRef<AppSelectOption[]>
  setAgentSchedule: (event: AppTextareaEvent) => void
  setPreferences: (event: AppTextareaEvent) => void
  save: () => Promise<void>
}

const AGENT_SCHEDULE_CLEARED = 'The final schedule cannot be emptied, only changed.'

export const useDisciplineActivation = (): DisciplineActivationReturn => {
  const route = useRoute()
  const toast = useToast()
  const disciplineStore = useDisciplineStore()
  const listsStore = useListsStore()
  const { refreshDiscipline } = useDisciplineRefresh()

  const activation = computed<DisciplineActivationData | null>(() => disciplineStore.getDetails?.activation_data ?? null)

  const refInitial = ref<DisciplineActivationForm>(createActivationForm(activation.value))
  const refForm = ref<DisciplineActivationForm>(createActivationForm(activation.value))
  const refIsSaving = ref<boolean>(false)

  const refKanbanUrl = useDisciplineKanbanUrl(() => disciplineStore.getDetails, DisciplineKanbanTab.Activation)

  /** As in legacy, no package (0) and a missing pace both read 0. */
  const pace = computed<number>(() => disciplineStore.getDetails?.last_package_pace || 0)
  const scheduleLines = computed<string[]>(() => getActivationScheduleLines(activation.value))
  const startDate = computed<string>(() => getActivationStartDate(activation.value))

  const expectationOptions = computed<AppSelectOption[]>(() =>
    Object.entries(getActivationExpectationLabels(listsStore.getLists?.activation.expectations))
      .map(([value, text]) => ({ value, text })))

  const refExpectations = computed<string[]>({
    get: () => refForm.value.expectations,
    set: (value) => {
      refForm.value.expectations = value
    },
  })

  const setAgentSchedule = (event: AppTextareaEvent): void => {
    refForm.value.agentSchedule = event.value
  }

  const setPreferences = (event: AppTextareaEvent): void => {
    refForm.value.preferences = event.value
  }

  const resetForm = (): void => {
    refInitial.value = createActivationForm(activation.value)
    refForm.value = createActivationForm(activation.value)
  }

  const save = async (): Promise<void> => {
    if (refIsSaving.value) return

    if (isActivationAgentScheduleCleared(activation.value, refForm.value)) {
      toast.warning(AGENT_SCHEDULE_CLEARED)
      return
    }

    const isSaved = await useDisciplineActivationUpdateApi({
      disciplineId: route.params.id as string,
      payload: buildActivationPayload(refInitial.value, refForm.value, activation.value?.agent_schedule ?? null),
      onStart: () => {
        refIsSaving.value = true
      },
      onFinish: () => {
        refIsSaving.value = false
      },
    })

    if (isSaved) await refreshDiscipline()
  }

  watch(activation, resetForm)

  return {
    refForm,
    refExpectations,
    refKanbanUrl,
    refIsSaving,
    pace,
    scheduleLines,
    startDate,
    expectationOptions,
    setAgentSchedule,
    setPreferences,
    save,
  }
}
